import { BufferAudio } from './buffer-audio';
import {
  LOOP_CHANNELS,
  SOUND_EVENTS,
  type Haptics,
  type SoundClip,
  type SoundEvent,
  type SoundPack,
  type SoundsSnapshot,
} from './sound-pack';

const bounded = (value: number) => (Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0);
const clipOf = (clip: SoundClip) =>
  typeof clip === 'string' ? { src: clip, gain: 1 } : { src: clip.src, gain: bounded(clip.volume ?? 1) };

/**
 * Optional feedback only. Neither audio completion nor animation controls game timing.
 * Reconciles preferences, the active pack and page lifecycle onto one BufferAudio.
 */
export class Sounds {
  private pack: SoundPack = {};
  private haptics: Haptics = {};
  private audio: BufferAudio | undefined;
  private contextRequested = false;
  private unlocked = false;
  private suspended = false;
  private rearmOnUnlock = false;
  effects = false;
  ambience = false;
  music = false;

  configure(pack: SoundPack, haptics: Haptics = {}): void {
    this.cancelHaptics();
    this.pack = { ...pack };
    this.haptics = { ...haptics };
    this.audio?.reset();
    this.sync();
  }
  unlock(): void {
    this.unlocked = true;
    // After backgrounding, iOS Safari can leave the context "running" with a dead render
    // thread; suspend/resume does not revive it. Recreate it inside the user gesture, both
    // on the first return tap and whenever the clock watchdog catches a frozen context.
    if (!this.suspended && this.audible() && this.audio && (this.rearmOnUnlock || this.audio.stalled())) {
      this.rearmOnUnlock = false;
      const context = this.createContext();
      if (context) this.audio.replaceContext(context);
    }
    this.sync();
  }
  setEffects(value: boolean): void {
    this.effects = value;
    this.sync();
  }
  setAmbience(value: boolean): void {
    this.ambience = value;
    this.sync();
  }
  setMusic(value: boolean): void {
    this.music = value;
    this.sync();
  }
  setSuspended(value: boolean): void {
    this.suspended = value;
    if (value) {
      if (this.audio && this.audible()) this.rearmOnUnlock = true;
      this.cancelHaptics();
    }
    this.sync();
  }

  play(event: SoundEvent): void {
    if (!this.unlocked || this.suspended) return;
    const pattern = this.haptics[event];
    if (pattern && typeof navigator !== 'undefined') {
      try {
        navigator.vibrate?.([...pattern]);
      } catch {
        /* unsupported haptics cannot stop play */
      }
    }
    const clip = this.pack[event];
    if (!this.effects || !clip) return;
    const { src, gain } = clipOf(clip);
    this.audio?.play(src, gain);
  }

  diagnosticsSnapshot(): SoundsSnapshot {
    return {
      unlocked: this.unlocked,
      suspended: this.suspended,
      rearmOnUnlock: this.rearmOnUnlock,
      context: this.audio?.diagnosticsSnapshot() ?? null,
    };
  }
  dispose(): void {
    this.cancelHaptics();
    this.audio?.dispose();
    this.audio = undefined;
    this.contextRequested = false;
    this.rearmOnUnlock = false;
    this.unlocked = false;
  }

  /** The one place that applies preferences, pack and lifecycle to the engine. */
  private sync(): void {
    const audible = this.unlocked && !this.suspended && this.audible();
    if (audible && !this.contextRequested) {
      // One attempt per lifetime: a refused AudioContext leaves the game silent, not retrying.
      this.contextRequested = true;
      const context = this.createContext();
      if (context) {
        this.audio = new BufferAudio(context);
      }
    }
    const audio = this.audio;
    if (!audio) return;
    for (const channel of LOOP_CHANNELS) {
      const clip = this.pack[channel];
      audio.setLoop(channel, this[channel] && clip ? clipOf(clip) : undefined);
    }
    audio.setEffectSources(this.effects ? this.effectSources() : []);
    if (!this.effects) audio.stopEffects();
    if (audible) audio.resume();
    else audio.stop();
  }
  private audible(): boolean {
    return (
      (this.effects && this.effectSources().length > 0) ||
      LOOP_CHANNELS.some((channel) => this[channel] && this.pack[channel])
    );
  }
  private effectSources(): string[] {
    return SOUND_EVENTS.flatMap((event) => {
      const clip = this.pack[event];
      return clip ? [clipOf(clip).src] : [];
    });
  }
  /** Undefined leaves the game silent where the browser refuses Web Audio. */
  private createContext(): AudioContext | undefined {
    if (typeof AudioContext === 'undefined') return undefined;
    try {
      return new AudioContext();
    } catch {
      return undefined;
    }
  }
  private cancelHaptics(): void {
    if (typeof navigator === 'undefined') return;
    try {
      navigator.vibrate?.(0);
    } catch {
      /* optional */
    }
  }
}
export const Sfx = new Sounds();
